/**
 * Third-party evidence collected for interview/task auto-approval.
 *
 * Stored in interview.metadata.evidence and submissionTask.result.evidence.
 * Multiple evidence entries can accumulate from different sources
 * (callbacks, webhooks, uploads).
 *
 * Single source of truth: the Zod schema (thirdPartyEvidenceSchema) lives
 * in @repo/db/types/task-result.ts. The type is derived from it via z.infer
 * and re-exported here for consumers that import from @repo/common.
 */

import { thirdPartyEvidenceSchema } from "@repo/db/types/task-result";
import type { z } from "zod";

export type ThirdPartyEvidence = z.infer<typeof thirdPartyEvidenceSchema>;

export { thirdPartyEvidenceSchema };

export interface InterviewEvidence {
  thirdParty?: ThirdPartyEvidence[];
}

/**
 * Helper to extract evidence from interview metadata.
 */
export function getInterviewEvidence(
  metadata: unknown,
): InterviewEvidence | null {
  if (!metadata || typeof metadata !== "object") {
    return null;
  }
  const m = metadata as Record<string, unknown>;
  if (!m.evidence || typeof m.evidence !== "object") {
    return null;
  }
  return m.evidence;
}

/**
 * Append a third-party evidence entry to existing metadata.
 * Returns a new metadata object (does not mutate).
 */
export function appendThirdPartyEvidence(
  existingMetadata: unknown,
  evidence: ThirdPartyEvidence,
): Record<string, unknown> {
  const metadata =
    existingMetadata && typeof existingMetadata === "object"
      ? { ...(existingMetadata as Record<string, unknown>) }
      : {};

  const existingEvidence =
    metadata.evidence && typeof metadata.evidence === "object"
      ? { ...(metadata.evidence as Record<string, unknown>) }
      : {};

  const existingThirdParty = Array.isArray(existingEvidence.thirdParty)
    ? [...existingEvidence.thirdParty]
    : [];

  existingThirdParty.push(evidence);

  return {
    ...metadata,
    evidence: {
      ...existingEvidence,
      thirdParty: existingThirdParty,
    },
  };
}
